// FRE-272: active-org client state.
//
// We persist the active org id in localStorage so it survives reloads even
// before Better Auth's session row updates land (the dashboard reads from
// localStorage and forwards `X-Active-Org`; the worker uses that header
// directly without needing a round-trip through Better Auth).
//
// `setActiveOrgId` also POSTs to Better Auth's org plugin so the session row
// stays in sync — this makes future calls without the header still resolve
// to the same org (e.g. PAT-less CLI calls in some future flow).

const STORAGE_KEY = "drop.activeOrgId";

export function getActiveOrgId(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // Private-mode Safari throws on localStorage access. Treat as "no hint";
    // the worker will fall back to session.activeOrganizationId / first org.
    return null;
  }
}

export function clearActiveOrgId(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Same Safari caveat — silent.
  }
}

/**
 * Write the active org id to localStorage AND tell Better Auth's organization
 * plugin to update `session.activeOrganizationId` server-side. The localStorage
 * hint propagates immediately via `X-Active-Org` on the next request; the
 * server-side write makes it durable even without the header.
 *
 * The returned promise resolves once the server has accepted the change, so
 * callers can `await` before re-running loaders / refetching data.
 */
export async function setActiveOrgId(organizationId: string): Promise<void> {
  try {
    window.localStorage.setItem(STORAGE_KEY, organizationId);
  } catch {
    // Even if localStorage is unavailable, push the change server-side so the
    // session row gets updated; reads on this page won't survive a reload
    // without storage but the active call will still take effect.
  }

  const res = await fetch("/api/auth/organization/set-active", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      origin: window.location.origin,
    },
    body: JSON.stringify({ organizationId }),
  });
  // Non-fatal: localStorage already updated. Log so we notice if Better Auth's
  // endpoint shape ever changes, but don't fail the UI flow.
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    console.warn(`set-active org failed (${res.status}):`, body);
  }
}
